/**
 * Actionable Set helpers
 */
import { stringLiteralArray } from "@oui/system/utils"
import { curry } from "ramda"

type Key = typeof keys[number]

const keys = stringLiteralArray([
  "Tab",
  "ArrowUp",
  "ArrowRight",
  "ArrowDown",
  "ArrowLeft"
])
const hasKey = curry((xs: Key[], event: React.KeyboardEvent) =>
  keys.filter(key => xs.includes(key)).some(key => event.key === key)
)
const hasArrowKey = hasKey(["ArrowUp", "ArrowRight", "ArrowDown", "ArrowLeft"])
const hasNextKey = hasKey(["ArrowDown", "ArrowRight"])

const actionableQuery = [
  "a[href]:not([disabled])",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input[type="text"]:not([disabled])',
  'input[type="radio"]:not([disabled])',
  'input[type="checkbox"]:not([disabled])',
  "select:not([disabled])"
].join(", ")

const isNumber = (n: string) => !isNaN(parseFloat(n))

const getAllActionables = (el: HTMLElement) =>
  Array.from(el.querySelectorAll(actionableQuery)) as HTMLElement[]

const getActionablesWithTabIndex = curry((tabIndex: 0 | -1, el: HTMLElement) =>
  getAllActionables(el).filter((el: HTMLElement) => {
    const idx = el.getAttribute("tabIndex") || "0"
    if (!isNumber(idx)) return false
    return parseFloat(idx) >= tabIndex
  })
)

const getActionables = (el: HTMLElement) => getActionablesWithTabIndex(-1, el)

const next = curry((loop: boolean, actionables: HTMLElement[]) => {
  const idx = actionables.indexOf(document.activeElement as HTMLElement)
  const activeElement = actionables[idx === -1 ? 0 : idx]
  if (!loop && idx === actionables.length - 1) return activeElement
  const nextElement =
    activeElement === actionables[actionables.length - 1]
      ? actionables[0]
      : actionables[idx + 1]
  nextElement.focus()
  return nextElement
})

const previous = curry((loop: boolean, actionables: HTMLElement[]) => {
  const idx = actionables.indexOf(document.activeElement as HTMLElement)
  const activeElement = actionables[idx]
  if (!loop && idx === 0) return activeElement
  const prevElement =
    activeElement === actionables[0]
      ? actionables[actionables.length - 1]
      : actionables[idx - 1]
  prevElement.focus()
  return prevElement
})

export {
  getActionables,
  getAllActionables,
  hasKey,
  hasArrowKey,
  hasNextKey,
  next,
  previous
}
